import React from 'react'
import type { InOutMap } from '../services/inOutService'
import { fetchInOutMap, setInOut } from '../services/inOutService'
import { useToast } from '@/shared/ui/Toast'

type Status = {
  isIn: boolean
  loading: boolean
  toggling: boolean
  toggle: () => Promise<void>
}

type InternalState = {
  initialized: boolean
  map: InOutMap
}

export type InOutUpdate = {
  opportunityId: number
  isIn: boolean
}

const inOutListeners = new Set<(update: InOutUpdate) => void>()

export function subscribeInOutUpdates(listener: (update: InOutUpdate) => void): () => void {
  inOutListeners.add(listener)
  return () => {
    inOutListeners.delete(listener)
  }
}

function notifyInOut(update: InOutUpdate) {
  inOutListeners.forEach((listener) => {
    try {
      listener(update)
    } catch {
      // noop
    }
  })
}

const InOutContext = React.createContext<{
  state: InternalState
  setState: React.Dispatch<React.SetStateAction<InternalState>>
} | null>(null)

export function InOutProvider(props: { children: React.ReactNode; opportunityIds: number[] }) {
  const { children, opportunityIds } = props
  const [state, setState] = React.useState<InternalState>({ initialized: false, map: {} })

  React.useEffect(() => {
    let active = true
    async function load() {
      try {
        const map = await fetchInOutMap(opportunityIds)
        if (!active) return
        setState({ initialized: true, map })
      } catch {
        if (!active) return
        setState({ initialized: true, map: {} })
      }
    }
    load()
    return () => {
      active = false
    }
  }, [opportunityIds])

  const value = React.useMemo(
    () => ({
      state,
      setState,
    }),
    [state],
  )

  return React.createElement(InOutContext.Provider, { value }, children as React.ReactElement)
}

export function useInOutStatus(opportunityId: number): Status {
  const ctx = React.useContext(InOutContext)
  const { add: toast } = useToast()
  const [toggling, setToggling] = React.useState(false)
  const [localInitialized, setLocalInitialized] = React.useState(false)
  const [localIsIn, setLocalIsIn] = React.useState(false)

  const usingContext = !!ctx

  // Carregamento local quando não há provider
  React.useEffect(() => {
    if (ctx) return
    let active = true
    ;(async () => {
      try {
        const map = await fetchInOutMap([opportunityId])
        if (!active) return
        const value = !!map[String(opportunityId)]
        setLocalIsIn(value)
        setLocalInitialized(true)
      } catch {
        if (!active) return
        setLocalIsIn(false)
        setLocalInitialized(true)
      }
    })()
    return () => {
      active = false
    }
  }, [ctx, opportunityId])

  const isIn = usingContext ? ctx.state.map[String(opportunityId)] ?? false : localIsIn
  const loading = usingContext ? !ctx.state.initialized : !localInitialized

  const toggle = React.useCallback(async () => {
    if (toggling) return
    setToggling(true)

    if (usingContext && ctx) {
      const current = ctx.state.map[String(opportunityId)] ?? false
      const next = !current
      ctx.setState((prev) => ({
        initialized: prev.initialized,
        map: { ...prev.map, [String(opportunityId)]: next },
      }))
      try {
        const result = await setInOut(opportunityId, next)
        ctx.setState((prev) => ({
          initialized: prev.initialized,
          map: { ...prev.map, [String(result.opportunityId)]: result.isIn },
        }))
        notifyInOut({ opportunityId: result.opportunityId, isIn: result.isIn })
      } catch (e: any) {
        ctx.setState((prev) => ({
          initialized: prev.initialized,
          map: { ...prev.map, [String(opportunityId)]: current },
        }))
        try {
          toast({
            title: 'Monitorar oportunidade',
            description:
              e?.message || 'Falha ao salvar sua preferência de notificações in-out. Tente novamente.',
            variant: 'error',
            soft: true,
          })
        } catch {}
      } finally {
        setToggling(false)
      }
      return
    }

    // Modo local (sem provider)
    const current = localIsIn
    const next = !current
    setLocalIsIn(next)
    try {
      const result = await setInOut(opportunityId, next)
      setLocalIsIn(result.isIn)
      notifyInOut({ opportunityId: result.opportunityId, isIn: result.isIn })
    } catch (e: any) {
      setLocalIsIn(current)
      try {
        toast({
          title: 'Monitorar oportunidade',
          description:
            e?.message || 'Falha ao salvar sua preferência de notificações in-out. Tente novamente.',
          variant: 'error',
          soft: true,
        })
      } catch {}
    } finally {
      setToggling(false)
    }
  }, [ctx, localIsIn, opportunityId, toggling, usingContext, toast])

  return {
    isIn,
    loading,
    toggling,
    toggle,
  }
}
